/**
 * 后台 - 视频渠道表单（创建/编辑）
 *
 * Provider 选项与默认参考图上限从后端 /video-models/providers/meta 加载，
 * 不在前端写死，确保后端新增 provider 时前端零改动。
 */

import { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  videoApi,
  type VideoProviderMetaInfo,
  type VideoProviderType,
} from '../services/videoApi';
import {
  videoChannelAdminApi,
  type VideoChannelAdminView,
} from '../services/videoAdminApi';
import toast from '../../../components/Toast';

interface Props {
  channel: VideoChannelAdminView | null;
  onClose: (changed: boolean) => void;
}

export default function VideoChannelForm({ channel, onClose }: Props) {
  const isEdit = !!channel;
  const [providers, setProviders] = useState<VideoProviderMetaInfo[]>([]);

  const [name, setName] = useState(channel?.name || '');
  const [provider, setProvider] = useState<VideoProviderType>(
    channel?.provider || 'apib-grok-imagine-1-5'
  );
  const [apiUrl, setApiUrl] = useState(channel?.apiUrl || '');
  const [apiModelId, setApiModelId] = useState(channel?.apiModelId || '');
  const [apiKey, setApiKey] = useState('');
  const [isActive, setIsActive] = useState(channel?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(channel?.sortOrder ?? 0);
  const [costPerCall, setCostPerCall] = useState(channel?.costPerCall ?? 0);
  const [remark, setRemark] = useState(channel?.remark ?? '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    videoApi
      .getProviderMetadata()
      .then(setProviders)
      .catch((err) => toast.error(err.message || '加载 provider 失败'));
  }, []);

  const currentProviderMeta = providers.find((p) => p.id === provider);

  // 切换 provider 时自动重置 apiModelId
  useEffect(() => {
    if (!currentProviderMeta) return;
    const supported = currentProviderMeta.supportedModelIds || [];
    if (supported.length > 0 && !supported.includes(apiModelId)) {
      setApiModelId(supported[0]);
    }
  }, [provider, currentProviderMeta]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('请填写渠道名称');
      return;
    }
    if (!apiUrl.trim()) {
      toast.error('请填写 API URL');
      return;
    }
    if (!isEdit && !apiKey.trim()) {
      toast.error('请填写 API Key');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        apiUrl: apiUrl.trim(),
        apiModelId: apiModelId.trim(),
        provider,
        isActive,
        sortOrder: Number(sortOrder) || 0,
        costPerCall: Number(costPerCall) || 0,
        remark: remark.trim() || null,
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      };

      if (isEdit && channel) {
        await videoChannelAdminApi.update(channel.id, payload);
        toast.success('已保存');
      } else {
        await videoChannelAdminApi.create(payload);
        toast.success('已创建');
      }
      onClose(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 px-4"
      onClick={() => onClose(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-light-text">
            {isEdit ? '编辑视频渠道' : '新增视频渠道'}
          </h3>
          <button onClick={() => onClose(false)} className="p-1.5 rounded-lg hover:bg-light-hover">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <FormField label="渠道名称">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field w-full"
              placeholder="比如：apimart-grok"
            />
          </FormField>

          <FormField label="Provider">
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as VideoProviderType)}
              className="input-field w-full"
            >
              {providers
                // 编辑既有渠道时保留当前 provider（即使被隐藏），新建时过滤掉隐藏的
                .filter((p) => !p.isHidden || p.id === provider)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                    {p.isHidden ? '（已隐藏）' : ''}
                  </option>
                ))}
            </select>
            {currentProviderMeta && (
              <p className="text-xs text-light-muted mt-1.5 leading-relaxed">
                {currentProviderMeta.adminCapability}
                <br />
                <span className="text-[11px]">{currentProviderMeta.adminDetail}</span>
              </p>
            )}
          </FormField>

          <FormField label="API URL">
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              className="input-field w-full font-mono text-sm"
              placeholder="https://api.apimart.ai"
            />
          </FormField>

          <FormField label="上游模型 ID（apiModelId）" hint="发给上游 API 的 model 字段值">
            {currentProviderMeta && currentProviderMeta.supportedModelIds.length > 0 ? (
              <select
                value={apiModelId}
                onChange={(e) => setApiModelId(e.target.value)}
                className="input-field w-full font-mono text-sm"
              >
                {currentProviderMeta.supportedModelIds.map((id) => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={apiModelId}
                onChange={(e) => setApiModelId(e.target.value)}
                className="input-field w-full font-mono text-sm"
                placeholder="如 grok-imagine-1.5-video-apimart"
              />
            )}
          </FormField>

          <FormField
            label="API Key"
            hint={isEdit ? '留空表示不修改' : undefined}
          >
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="input-field w-full font-mono text-sm"
              placeholder={isEdit ? channel?.apiKeyMasked || '****' : 'sk-***'}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="排序">
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                className="input-field w-full"
              />
            </FormField>
            <FormField label="单次成本（仅记录）">
              <input
                type="number"
                step={0.001}
                value={costPerCall}
                onChange={(e) => setCostPerCall(Number(e.target.value))}
                className="input-field w-full"
              />
            </FormField>
          </div>

          <FormField label="备注">
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={2}
              className="input-field w-full"
            />
          </FormField>

          <label className="flex items-center gap-2 text-sm text-light-text cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4"
            />
            启用此渠道
          </label>
        </div>

        <div className="px-6 py-4 border-t border-light-border flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="btn-secondary">
            取消
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="btn-primary">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-light-text block mb-1.5">
        {label}
        {hint && <span className="text-xs text-light-muted ml-2">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
